import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import Modal from './Modal';
import Toggle from './Toggle';
import ImageField from './ImageField';
import Loader from '../Components/Loader/Loader';

const EMPTY = {
  title: '',
  slug: '',
  category: 'General',
  excerpt: '',
  content: '',
  author: 'Amaanat IVF Team',
  tags: '',
  meta_description: '',
  is_published: true,
};

export default function Blogs() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [pendingImages, setPendingImages] = useState([]); // Files chosen before first save
  const [gallery, setGallery] = useState([]); // saved gallery rows for editing post
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/admin/blogs');
      setList(d.posts || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing({ ...EMPTY, id: 0 });
    setCoverFile(null);
    setPendingImages([]);
    setGallery([]);
  };

  const startEdit = (p) => {
    setEditing({
      id: p.id,
      title: p.title || '',
      slug: p.slug || '',
      category: p.category || 'General',
      excerpt: p.excerpt || '',
      content: p.content || '',
      author: p.author || 'Amaanat IVF Team',
      tags: p.tags || '',
      meta_description: p.meta_description || '',
      is_published: Boolean(p.is_published),
      cover_image_url: p.cover_image_url || null,
    });
    setCoverFile(null);
    setPendingImages([]);
    setGallery([]);
    // Load the saved gallery for existing posts.
    if (p.id > 0) {
      api(`/admin/blogs/gallery?blog_id=${p.id}`)
        .then((d) => setGallery(d.images || []))
        .catch(() => {});
    }
  };

  const uploadGallery = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (editing.id <= 0) {
      // Not saved yet: hold locally, attach after first save.
      setPendingImages((prev) => [...prev, ...files]);
      return;
    }
    setGalleryBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('blog_id', String(editing.id));
      files.forEach((f) => fd.append('images', f));
      await api('/admin/blogs/gallery', { method: 'POST', body: fd });
      const d = await api(`/admin/blogs/gallery?blog_id=${editing.id}`);
      setGallery(d.images || []);
      load();
    } catch (e) {
      setError(e.message);
    }
    setGalleryBusy(false);
  };

  const deleteGalleryImage = async (imageId) => {
    setGalleryBusy(true);
    setError('');
    try {
      await api('/admin/blogs/gallery/delete', {
        method: 'POST',
        body: JSON.stringify({ image_id: imageId }),
      });
      setGallery((g) => g.filter((x) => x.id !== imageId));
    } catch (e) {
      setError(e.message);
    }
    setGalleryBusy(false);
  };

  const removeCover = async () => {
    if (editing.id <= 0) return;
    await api('/admin/blogs/delete-image', {
      method: 'POST',
      body: JSON.stringify({ id: editing.id }),
    });
    setEditing((e) => ({ ...e, cover_image_url: null }));
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const fd = new FormData();
    Object.entries(editing).forEach(([k, v]) => {
      if (k === 'cover_image_url') return;
      // Booleans must serialize as '1'/'' — raw false becomes the STRING
      // "false", which the backend's truthiness check reads as true.
      if (typeof v === 'boolean') fd.append(k, v ? '1' : '');
      else fd.append(k, v);
    });
    if (coverFile) fd.append('cover_image', coverFile);
    try {
      const res = await api('/admin/blogs/save', { method: 'POST', body: fd });

      // First save with locally-held gallery images: upload them now.
      if (pendingImages.length > 0 && res.post && res.post.id) {
        const gfd = new FormData();
        gfd.append('blog_id', String(res.post.id));
        pendingImages.forEach((f) => gfd.append('images', f));
        await api('/admin/blogs/gallery', { method: 'POST', body: gfd });
      }

      setEditing(null);
      setCoverFile(null);
      setPendingImages([]);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api('/admin/blogs/delete', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggle = async (id) => {
    try {
      await api('/admin/blogs/toggle', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k) => (e) =>
    setEditing((prev) => ({
      ...prev,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const close = () => {
    setEditing(null);
    setCoverFile(null);
    setPendingImages([]);
  };

  return (
    <div>
      <div className="admin-page-head">
        <h1 className="admin-page-title">Blogs</h1>
        <button className="admin-btn admin-btn-primary" onClick={startNew}>
          + New post
        </button>
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {editing && (
        <Modal
          title={editing.id ? `Edit post #${editing.id}` : 'New post'}
          onClose={close}
          wide
        >
          <form className="admin-form" onSubmit={save}>
            <div className="admin-form-grid">
              <label>
                Title *
                <input value={editing.title} onChange={set('title')} required />
              </label>
              <label>
                Slug (blank = from title)
                <input value={editing.slug} onChange={set('slug')} />
              </label>
              <label>
                Category
                <input value={editing.category} onChange={set('category')} />
              </label>
              <label>
                Author
                <input value={editing.author} onChange={set('author')} />
              </label>
              <label>
                Tags (comma separated)
                <input value={editing.tags} onChange={set('tags')} />
              </label>
            </div>

            <ImageField
              label="Cover image"
              url={editing.cover_image_url || null}
              file={coverFile}
              onPick={(f) => setCoverFile(f)}
              onRemove={removeCover}
            />

            <label className="admin-form-full">
              Excerpt
              <textarea rows={2} value={editing.excerpt} onChange={set('excerpt')} />
            </label>

            <label className="admin-form-full">
              Content
              <textarea
                className="admin-content-textarea"
                rows={12}
                placeholder="Write the article here. Blank lines start a new paragraph."
                value={editing.content}
                onChange={set('content')}
              />
              <small className="admin-field-hint">
                Plain text — blank lines create paragraphs. Pictures go in the
                gallery below.
              </small>
            </label>

            <label className="admin-form-full">
              Meta description
              <textarea rows={2} value={editing.meta_description} onChange={set('meta_description')} />
            </label>

            {/* Gallery */}
            <div className="admin-form-full admin-gallery-section">
              <div className="admin-gallery-head">
                <h3>Picture gallery</h3>
                {editing.id > 0 && (
                  <label className="admin-btn admin-btn-sm">
                    {galleryBusy ? 'Uploading…' : '+ Upload pictures'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      disabled={galleryBusy}
                      onChange={(e) => {
                        uploadGallery(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              {editing.id <= 0 ? (
                <>
                  {pendingImages.length === 0 ? (
                    <p className="admin-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                      Save the post first, or pick pictures now — they upload
                      right after the first save.
                    </p>
                  ) : (
                    <div className="admin-gallery-grid">
                      {pendingImages.map((f, i) => (
                        <div className="admin-gallery-item" key={i}>
                          <img src={URL.createObjectURL(f)} alt={f.name} />
                          <button
                            type="button"
                            className="admin-gallery-del"
                            title="Remove"
                            onClick={() => setPendingImages((p) => p.filter((_, j) => j !== i))}
                          >
                            ×
                          </button>
                          <span className="admin-gallery-badge">Queued</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {gallery.length === 0 && !galleryBusy && (
                    <p className="admin-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                      No pictures yet. Use “+ Upload pictures” — you can select
                      several at once.
                    </p>
                  )}
                  {gallery.length > 0 && (
                    <div className="admin-gallery-grid">
                      {gallery.map((g) => (
                        <div className="admin-gallery-item" key={g.id}>
                          <img src={g.url} alt="Gallery image" />
                          <button
                            type="button"
                            className="admin-gallery-del"
                            title="Delete image"
                            disabled={galleryBusy}
                            onClick={() => deleteGalleryImage(g.id)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="admin-form-checks">
              <label>
                <input type="checkbox" checked={editing.is_published} onChange={set('is_published')} />
                Published
              </label>
            </div>
            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">
                Save
              </button>
              <button type="button" className="admin-btn" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {loading ? (
        <Loader inline status="Loading posts" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Category</th>
              <th>Author</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.title}</td>
                <td>{p.category}</td>
                <td>{p.author}</td>
                <td>
                  <Toggle checked={p.is_published} onChange={() => toggle(p.id)} />
                </td>
                <td className="admin-actions">
                  <button className="admin-btn admin-btn-sm" onClick={() => startEdit(p)}>
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn-sm admin-btn-danger"
                    onClick={() => remove(p.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
